/* eslint-disable no-underscore-dangle */
import { HVButton, HVIcon, HVText, useHVSnackbar } from "@repo/HVComponents";
import ModalManager from "@utils/ModalManager";
import { useLang } from "@hooks/useLang";
import { useEffect, useRef, useState } from "react";
import _ from "lodash";
import { FilterType } from "@queries/tanstack/analytics-filter/AnalyticsFilterQuery";
import useDetectOutsideClick from "@hooks/useDetectOutsideClick";
import { ICON } from "src/constants/icons";
import * as S from "../AnalyticsPage.styled";
import EditableInput from "./EditableInput";

type Props = {
  searchFilter?: FilterType;
  toggleCollapse?: boolean;
  setToggleCollapse?: (value: boolean) => void;
  onUpdateFilterName?: (id: string, value: string) => void;
  onRemove?: (id: string) => void;
};

const AnalyticsHeader = ({
  searchFilter,
  setToggleCollapse,
  toggleCollapse,
  onUpdateFilterName,
  onRemove
}: Props) => {
  const { translate } = useLang();

  const [isEditFilterNameEnabled, setIsEditFilterNameEnabled] = useState(false);
  const contextMenuRef = useRef<HTMLDivElement>(null);
  const { showSnackbar } = useHVSnackbar();

  const [contextMenu, setContextMenu] = useState<boolean>(false);
  useDetectOutsideClick(contextMenuRef, () => setContextMenu(false));

  const isValidName = (name: string) => {
    return !_.isEmpty(name.trim());
  };

  const handleFilterNameChange = (value?: string) => {
    setIsEditFilterNameEnabled(false);
    if (searchFilter?._id) {
      const filterName = !value ? searchFilter.filterName : value;

      if (isValidName(filterName)) {
        onUpdateFilterName?.(searchFilter?._id, filterName);
      } else {
        showSnackbar({
          id: "enter-right-name",
          message: translate("analytics_enter_right_name"),
          variant: "fail"
        });
      }
    } else {
      showSnackbar({
        id: "filter-id-not-found",
        message: "filter id not found",
        variant: "fail"
      });
    }
  };

  useEffect(() => {
    setIsEditFilterNameEnabled(false);
  }, [searchFilter?._id]);

  return (
    <S.Header>
      <S.HeaderSection>
        <HVButton
          onClick={() => setToggleCollapse?.(!toggleCollapse)}
          data-testid="collapse-out"
          aria-label="collapse out"
          variant="Tertiary"
          buttonContainerProps={{
            style: {
              height: 32,
              width: 32,
              padding: 0
            }
          }}
        >
          {toggleCollapse ? (
            <HVIcon
              name="panel_collapse"
              scale="large"
              theme="line"
              svgProps={{ ...ICON.SIZE_20, ...ICON.COLOR_STROKE }}
            />
          ) : (
            <HVIcon
              name="panel_expansion"
              scale="large"
              theme="line"
              svgProps={{ ...ICON.SIZE_20, ...ICON.COLOR_STROKE }}
            />
          )}
        </HVButton>
        <EditableInput
          isEditing={isEditFilterNameEnabled}
          onSubmit={handleFilterNameChange}
          value={searchFilter?._id ? searchFilter?.filterName : undefined}
        />
      </S.HeaderSection>
      <S.HeaderSection>
        <HVButton
          variant="Ghost"
          buttonContainerProps={{
            style: {
              height: 32,
              width: 32,
              padding: 0
            }
          }}
          onClick={() => setContextMenu(true)}
        >
          <HVIcon name="more" size="20" />
        </HVButton>
        {contextMenu && (
          <S.ContextMenu ref={contextMenuRef}>
            <HVButton
              variant="Ghost"
              buttonContainerProps={{
                h: 32,
                styles: {
                  inner: {
                    display: "block"
                  }
                },
                disabled: searchFilter?._id === undefined
              }}
              onClick={() => {
                setIsEditFilterNameEnabled(true);
                setContextMenu(false);
              }}
            >
              <HVText type="Body-sm" c="var(--hv-text-normal-primary)">
                {translate("edit_name")}
              </HVText>
            </HVButton>
            <HVButton
              variant="Ghost"
              buttonContainerProps={{
                h: 32,
                styles: {
                  inner: {
                    display: "block"
                  }
                },
                disabled: searchFilter?._id === undefined
              }}
              onClick={() => {
                ModalManager.confirm(translate("analytics_remove_desc"), {
                  title: translate("analytics_remove_title"),
                  labelConfirm: translate("remove"),
                  labelCancel: translate("cancel"),
                  onSubmit: () => {
                    setContextMenu(false);
                    if (searchFilter?._id) {
                      onRemove?.(searchFilter?._id);
                    }
                  }
                });
              }}
            >
              <HVText type="Body-sm" c="var(--hv-data-red)">
                {translate("remove")}
              </HVText>
            </HVButton>
          </S.ContextMenu>
        )}
      </S.HeaderSection>
    </S.Header>
  );
};

export default AnalyticsHeader;
